import React, { useState, useRef } from 'react';
import { Button, List, Space, Typography, Card } from 'antd'; // Using Antd for components
import { AudioOutlined, DeleteOutlined, PauseOutlined, PlayCircleOutlined, SendOutlined } from '@ant-design/icons'; // Antd Icons
import Style from './VoiceOrderTaker.module.css'
const { Text, Title } = Typography;

// --- PLACEHOLDER: Integrate your Farsi ASR API here ---
// You will need to replace this with a real API call (e.g., Axios to Google Cloud Speech-to-Text)
const transcribeFarsiAudio = async (audioBlob) => {
    // 1. Convert Blob to Base64 (many APIs require this)
    const base64Audio = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(audioBlob);
    });

    // 2. Perform API call here (e.g., using axios)
    console.log("Simulating API call for Farsi transcription...");
    // await axios.post('YOUR_TRANSCRIPTION_API_ENDPOINT', { audio: base64Audio, language: 'fa-IR' });
    
    // --- Mock Transcription Text for demonstration ---
    const mockTranscriptions = [
        "سلام، یک پیتزا مارگاریتا می‌خواهم.", // "Hello, I want a Margarita pizza."
        "لطفا نوشابه و سالاد هم اضافه کنید.", // "Please add soda and salad."
        "سفارش من را به آدرس قبلی بفرستید.", // "Send my order to the previous address."
    ];
    return mockTranscriptions[Math.floor(Math.random() * mockTranscriptions.length)];
};
// -----------------------------------------------------


/**
 * Renders the voice ordering interface.
 */
const VoiceOrderTaker = () => {
    // State to hold all recorded voice notes
    const [voiceNotes, setVoiceNotes] = useState([]);
    // State for managing the recording process
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    
    // References for the MediaRecorder and audio chunks
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    // --- Voice Recording Functions ---
    
    const startRecording = async () => {
        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            
            // Create a new MediaRecorder instance
            mediaRecorderRef.current = new MediaRecorder(stream);
            
            // Event handler for when data is available (chunks of audio)
            mediaRecorderRef.current.ondataavailable = (event) => {
                audioChunksRef.current.push(event.data);
            };

            // Event handler for when recording stops
            mediaRecorderRef.current.onstop = async () => {
                setIsProcessing(true);
                // Combine all chunks into a single Blob
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm; codecs=opus' });
                audioChunksRef.current = []; // Clear chunks for next recording
                
                // Transcribe the audio
                const text = await transcribeFarsiAudio(audioBlob);
                
                // Create a URL for playback
                const audioUrl = URL.createObjectURL(audioBlob);
                
                // Add the new note to the list
                const newNote = {
                    id: Date.now(),
                    audioUrl,
                    transcription: text,
                    audioBlob, // Keep the blob for final submission if needed
                };
                setVoiceNotes((prevNotes) => [...prevNotes, newNote]);
                
                // Stop the microphone stream
                stream.getTracks().forEach(track => track.stop());
                setIsProcessing(false);
            };

            // Start recording
            mediaRecorderRef.current.start();
            setIsRecording(true);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            alert('Cannot access microphone. Please check permissions.');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    // --- Voice Note Management Functions ---

    const handleDelete = (id) => {
        setVoiceNotes((prevNotes) => prevNotes.filter((note) => note.id !== id));
    };

    const handleCompleteOrder = () => {
        if (voiceNotes.length === 0) {
            alert("Please record at least one voice note for your order.");
            return;
        }

        // 1. Prepare data for submission
        const orderData = voiceNotes.map(note => ({
            transcription: note.transcription,
            // You can choose to send the audio URL, the Blob itself, 
            // or maybe just the text if your backend doesn't need the audio.
            // For a complete order, you'd likely process and upload the audioBlob to Firebase Storage here.
            audioUrl: note.audioUrl,
            // To upload to Firebase, you'd use note.audioBlob
        }));

        console.log("Order Data Ready for Submission:", orderData);
        alert(`Order Completed! Sent ${voiceNotes.length} voice notes. Check console for data.`);

        // 2. Here you would use Axios to send the data to your backend
        // Example: 
        // axios.post('/api/orders', { notes: orderData, userId: '...' })
        //   .then(response => console.log('Order sent successfully!'))
        //   .catch(error => console.error('Order failed to send', error));

        // Clear notes after submission (optional)
        // setVoiceNotes([]); 
    };

    // --- Voice Note Rendering Component ---

    const VoiceNoteItem = ({ note }) => (
        <List.Item
            actions={[
                <Button 
                    key="delete" 
                    danger 
                    icon={<DeleteOutlined />} 
                    onClick={() => handleDelete(note.id)}
                    type="text"
                >
                    حذف
                </Button> // "Delete"
            ]}
        >
            <List.Item.Meta
                title={<Text strong>پیام صوتی:</Text>} // "Voice Message:"
                description={
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {/* Audio Playback Element */}
                        <audio controls src={note.audioUrl} style={{ width: '100%', minWidth: '250px' }}>
                            مرورگر شما از پخش صدا پشتیبانی نمی‌کند.
                        </audio>
                        
                        {/* Transcribed Text */}
                        <Card size="small" style={{ marginTop: '8px', backgroundColor: '#f5f5f5', borderRight: '3px solid #1890ff' }}>
                             <Text type="secondary" style={{ display: 'block', marginBottom: '4px' }}>متن رونویسی شده:</Text> 
                             <Text dir="rtl" style={{ fontSize: '16px' }}>{note.transcription}</Text>
                        </Card>
                    </Space>
                }
            />
        </List.Item>
    );

    // --- Main Component Render ---

    return (
        <div className={Style.ComponentHolder}>
            <Space className={Style.spacestyle}>
                <Button
                    type={isRecording ? "default" : "primary"}
                    size="large"
                    icon={isRecording ? <PauseOutlined /> : <AudioOutlined />}
                    onClick={isRecording ? stopRecording : startRecording}
                    loading={isProcessing}
                    disabled={isProcessing}
                    style={{ minWidth: '150px' }}
                >
                    {isProcessing ? "در حال پردازش..." : isRecording ? "پایان ضبط" : "شروع ضبط سفارش شما"} 
                    {/* "Processing..." : "Stop Recording" : "Start Recording" */}
                </Button>
            </Space>

            {/* --- Voice Notes List --- */}
            <Title level={4} style={{ borderBottom: '1px solid #eee', paddingBottom: '10px' }}>پیام‌های صوتی شما ({voiceNotes.length})</Title>
            
            {voiceNotes.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#888' }}>هیچ پیام صوتی ثبت نشده است.</p> // "No voice messages recorded."
            ) : (
                <List
                    itemLayout="horizontal"
                    dataSource={voiceNotes}
                    renderItem={(note) => <VoiceNoteItem key={note.id} note={note} />}
                />
            )}

            {/* --- Complete Order Button --- */}
            <footer className={Style.footerStyle}>
                <div className={Style.ButtonHolder}>
                    <Button
                        type="primary"
                        size="large"
                        icon={<SendOutlined />}
                        onClick={handleCompleteOrder}
                        disabled={voiceNotes.length === 0}
                        style={{ width: '100%',background:"#bd0065",color:"#fff",border:"none" }} 
                    >
                        تکمیل و ارسال سفارش
                    </Button>
                </div>
            </footer>
        </div>
    );
};

export default VoiceOrderTaker;